import { PhoneCallbackOutlined, SwapVert } from '@mui/icons-material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import StopIcon from '@mui/icons-material/Stop';
import { Autocomplete, Avatar, Box, Chip, IconButton, TextField, Typography } from '@mui/material';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import { useHistory, useLocation } from 'react-router-dom';
import useDebounce from '../../../hooks/useDebounce';
import { Pagination } from '../../../interfaces/common';
import {
  FindAllVoicemailsParams,
  VOICEMAIL_STATUS_COLORS,
  VOICEMAIL_STATUS_DISPLAY,
  VoicemailDirection,
  VoicemailLog,
  VoicemailStatus,
} from '../../../interfaces/voicemail';
import voiceService from '../../../services/appointments/calls';
import coreService from '../../../services/core';
import { formatWithTimezone } from '../../../util/dateFormat';
import { CustomTable } from '../../common/CustomTable';
import { PhoneOutgoing } from '../../common/PhoneOutgoing';
import { ClinicFilter } from '../ClinicFilter';
import AgentsFilter from '../filters/AgentsFilter';
import { SimplifiedMember } from '../messages/ChatDrawer/ChatDrawer';
import { VoicemailDetailModal } from './VoicemailDetailModal/VoicemailDetailModal';
import './voicemail-center.css';

type Props = {
  breadcrumbs: ReactNode;
};

const INITIAL_PAGINATION_STATE = {
  currentPage: 1,
  perPage: 10,
  totalCount: 0,
  totalPages: 0,
};

const DEFAULT_PAGE_SIZE = 10;

export const VoicemailCenter = ({ breadcrumbs }: Props) => {
  const [voicemails, setVoicemails] = useState<VoicemailLog[]>([]);
  const [page, setPage] = useState<number>(1);
  const [filters, setFilters] = useState<FindAllVoicemailsParams>({
    status: [VoicemailStatus.PENDING],
  });
  const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION_STATE);
  const [selectedVoicemail, setSelectedVoicemail] = useState<VoicemailLog | null>(null);
  const [members, setMembers] = useState<SimplifiedMember[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentAudio, setCurrentAudio] = useState<HTMLAudioElement | null>(null);
  const [playingVoicemailId, setPlayingVoicemailId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const isMountedRef = useRef<boolean>(true);

  const notify = useNotify();
  const location = useLocation();
  const history = useHistory();

  // `?voicemailId=<id>` deep link, used by the voicemail notification email. The email used to
  // link straight to the recording in S3; that bucket is closed now (EH-1399), and landing on the
  // bare list was not a substitute because it defaults to PENDING and paginates, so the voicemail
  // the email is about is often not on screen.
  const deepLinkedVoicemailId = useMemo(() => {
    const raw = new URLSearchParams(location.search).get('voicemailId');
    const parsed = Number(raw);

    return raw && Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [location.search]);

  // Which deep link we have already acted on, so closing the modal does not immediately reopen it.
  const openedDeepLinkRef = useRef<number | null>(null);

  const clearDeepLink = useCallback(() => {
    const params = new URLSearchParams(location.search);

    if (!params.has('voicemailId')) {
      return;
    }

    params.delete('voicemailId');
    const query = params.toString();

    history.replace(`${location.pathname}${query ? `?${query}` : ''}`);
  }, [history, location.pathname, location.search]);

  const fetchVoicemails = useCallback(async () => {
    try {
      setLoading(true);

      // Clean up filters - remove empty strings and undefined values
      const cleanedFilters = Object.entries(filters).reduce((acc, [key, value]) => {
        if (value !== '' && value !== undefined && value !== null) {
          if (Array.isArray(value) && value.length === 0) {
            return acc;
          }
          acc[key] = value;
        }
        return acc;
      }, {} as any);

      const response = await voiceService.searchVoicemails({
        page: page,
        pageSize: pagination.perPage,
        ...cleanedFilters,
      });

      if (!isMountedRef.current) {
        return;
      }

      if (!response.data || !response.pagination) {
        notify('Failed to fetch voicemails', 'error');
        return;
      }

      setVoicemails(response.data);
      setPagination(response.pagination);
    } catch (error) {
      if (!isMountedRef.current) {
        return;
      }
      notify('Failed to fetch voicemails', 'error');
    }

    if (isMountedRef.current) {
      setLoading(false);
    }
  }, [filters, pagination.perPage, notify, page]);

  const formatDuration = (voicemail: VoicemailLog) => {
    if (typeof voicemail.duration !== 'number' || isNaN(voicemail.duration)) return '-';
    const minutes = Math.floor(voicemail.duration / 60);
    const seconds = voicemail.duration % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleFilterChanges = (filter?: {
    [filter: string]: boolean | number | string | undefined | string[] | number[];
  }) => {
    const updatedFilters = {
      ...filters,
      ...filter,
    };

    // Clean up empty date strings
    if (updatedFilters.startDate === '') {
      delete updatedFilters.startDate;
    }
    if (updatedFilters.endDate === '') {
      delete updatedFilters.endDate;
    }

    setFilters(updatedFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleRefresh = useCallback(() => {
    setPage(1);
    fetchVoicemails();
  }, [fetchVoicemails]);

  const searchUsersByTerm = useCallback(
    async ({
      searchTerm,
      target,
    }: {
      searchTerm?: string | null;
      target: 'providers' | 'members';
    }) => {
      if (!searchTerm) {
        return [];
      }
      const { data } = await coreService.fetchMembers({ searchTerm, target });
      return data.map((member) => ({
        id: member.id,
        phone: member.phone,
        name: member.fullName,
      }));
    },
    [],
  );

  const debouncedFetchMembers = useDebounce(
    useCallback(
      async (param: { searchTerm: string }) => {
        setMembers(await searchUsersByTerm({ ...param, target: 'members' }));
      },
      [searchUsersByTerm],
    ),
  );

  const handlePlayVoicemail = useCallback(
    (voicemail: VoicemailLog) => {
      // Stop any currently playing audio
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }

      if (!voicemail.recordingUrl) {
        notify('No recording available', 'warning');
        return;
      }

      if (playingVoicemailId === voicemail.id) {
        // Stop playing if same voicemail is clicked
        setPlayingVoicemailId(null);
        setCurrentAudio(null);
        return;
      }

      const audio = new Audio(voicemail.recordingUrl);

      audio.onplay = () => {
        setPlayingVoicemailId(voicemail.id);
      };

      audio.onended = () => {
        setPlayingVoicemailId(null);
        setCurrentAudio(null);
      };

      audio.onerror = () => {
        notify('Error playing voicemail', 'error');
        setPlayingVoicemailId(null);
        setCurrentAudio(null);
      };

      audio.play().catch(() => {
        notify('Error playing voicemail', 'error');
      });

      setCurrentAudio(audio);
    },
    [currentAudio, playingVoicemailId, notify],
  );

  const handleVoicemailClick = useCallback((voicemail: VoicemailLog) => {
    setSelectedVoicemail(voicemail);
    setIsDetailModalOpen(true);
  }, []);

  // Resolve the deep-linked voicemail on its own rather than looking for it in `voicemails`: the
  // active filters and page have nothing to do with which voicemail the email was about. The
  // server applies the same clinic-scoping as the list, so an id the caller may not see 404s.
  useEffect(() => {
    if (!deepLinkedVoicemailId) {
      openedDeepLinkRef.current = null;
      return;
    }

    if (openedDeepLinkRef.current === deepLinkedVoicemailId) {
      return;
    }

    openedDeepLinkRef.current = deepLinkedVoicemailId;
    let cancelled = false;

    const openDeepLinkedVoicemail = async () => {
      const response = await voiceService.getVoicemailById(deepLinkedVoicemailId);

      if (cancelled || !isMountedRef.current) {
        return;
      }

      if (!response.success || !response.data) {
        notify('That voicemail is unavailable, or you do not have access to it', 'warning');
        clearDeepLink();
        return;
      }

      setSelectedVoicemail(response.data);
      setIsDetailModalOpen(true);
    };

    openDeepLinkedVoicemail();

    return () => {
      cancelled = true;
    };
  }, [deepLinkedVoicemailId, notify, clearDeepLink]);

  const handleVoicemailUpdate = useCallback((updatedVoicemail: VoicemailLog) => {
    setVoicemails((prev) =>
      prev.map((vm) => (vm.id === updatedVoicemail.id ? updatedVoicemail : vm)),
    );
    setSelectedVoicemail(updatedVoicemail);
  }, []);

  const isInboundDirection = (voicemail: VoicemailLog) => {
    if (!voicemail.direction) {
      // Default is true
      return true;
    }

    return voicemail.direction === VoicemailDirection.INBOUND;
  };

  useEffect(() => {
    isMountedRef.current = true;

    fetchVoicemails();

    return () => {
      isMountedRef.current = false;
    };
  }, [filters, fetchVoicemails, page]);

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    };
  }, [currentAudio]);

  // Memoized rows for CustomTable
  const rows = useMemo(
    () =>
      voicemails.map((voicemail) => {
        const isInbound = isInboundDirection(voicemail);

        return {
          onClick: () => handleVoicemailClick(voicemail),
          action: isInbound ? (
            <PhoneCallbackOutlined sx={{ color: '#AF77BE' }} />
          ) : (
            <PhoneOutgoing sx={{ color: '#389FBA' }} />
          ),
          from: (
            <Box flexDirection='column' display='flex'>
              <Typography variant='body2'>
                {voicemail.fromName || (isInbound ? 'Member' : 'Agent')}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {voicemail.fromNumber}
              </Typography>
            </Box>
          ),
          to: (
            <Box flexDirection='column' display='flex'>
              <Typography variant='body2'>
                {voicemail.toName || (isInbound ? 'Agent' : 'Member')}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {voicemail.toNumber}
              </Typography>
            </Box>
          ),
          createdAt: (
            <Box display='flex' flexDirection='column'>
              <Typography variant='body2'>
                {voicemail.createdAt ? formatWithTimezone(voicemail.createdAt) : ''}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {voicemail.createdAt
                  ? formatWithTimezone(voicemail.createdAt, undefined, 'HH:mm Z')
                  : ''}
              </Typography>
            </Box>
          ),
          voicemail: (
            <Box display='flex' alignItems='center' gap={1}>
              <Box
                className={`voicemail-mini-player ${
                  playingVoicemailId === voicemail.id ? 'playing' : 'idle'
                }`}>
                <IconButton
                  size='small'
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlayVoicemail(voicemail);
                  }}
                  disabled={!voicemail.recordingUrl}>
                  {playingVoicemailId === voicemail.id ? <StopIcon /> : <PlayArrowIcon />}
                </IconButton>
                <Box className='voicemail-mini-player-separator'></Box>
                <Typography variant='body2'>{formatDuration(voicemail)}</Typography>
              </Box>
            </Box>
          ),
          status: (
            <Chip
              size='small'
              label={VOICEMAIL_STATUS_DISPLAY[voicemail.status]}
              sx={{
                fontSize: '12px',
                backgroundColor: VOICEMAIL_STATUS_COLORS[voicemail.status],
                color: 'white',
              }}
            />
          ),
          assignee: (
            <Box flexDirection='column' display='flex'>
              <Typography variant='body2' className='voicemail-assignee'>
                {voicemail.assignee?.name || ''}
              </Typography>
              <Typography variant='caption' color='textSecondary'>
                {voicemail.assignee ? `ID:${voicemail.assignee.coreProviderId}` : '-'}
              </Typography>
            </Box>
          ),
          notes: (
            <Typography variant='body2' className='voicemail-notes'>
              {voicemail.notes || '-'}
            </Typography>
          ),
        };
      }),
    [voicemails, playingVoicemailId, handleVoicemailClick, handlePlayVoicemail],
  );

  return (
    <Box className='tab-section voicemail-center-section' marginTop={0}>
      <Box className='voicemail-center-header'>
        <Box display='flex' justifyContent='space-between' alignContent='center' marginBottom='8px'>
          {breadcrumbs}
        </Box>

        <Box display='flex' alignItems='flex-start' gap={1} mt={2} mb={3} flexWrap='wrap'>
          {/* Member Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Member</Typography>
            <Autocomplete
              fullWidth
              className='member-selector'
              options={members}
              filterOptions={(x) => x}
              getOptionLabel={(option) => `${option.name} ID: ${option.id}`}
              onChange={(_, v) => handleFilterChanges({ coreMemberId: v?.id || undefined })}
              noOptionsText='No members found'
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              value={members.find((m) => m.id === filters.coreMemberId) || null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant='outlined'
                  placeholder='Enter a member name'
                  onChange={(e) => debouncedFetchMembers({ searchTerm: e.target.value })}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <Avatar sx={{ color: 'action.active', mr: 1 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component='li' {...props}>
                  <Avatar sx={{ mr: 2, bgcolor: '#007bff' }}>
                    {`${option.name?.[0] ?? ''}${option.name?.[1] ?? ''}`.toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography fontSize={'0.8rem'}>{option.name}</Typography>
                    <Typography variant='caption' color='textSecondary'>
                      ID: {option.id}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
          </Box>

          {/* Agent Filter */}
          <AgentsFilter
            value={filters.coreProviderId}
            filterType='coreProviderId'
            handleFilterChanges={handleFilterChanges}
          />

          {/* Assignee Filter */}
          <AgentsFilter
            value={filters.assigneeId}
            filterType='assigneeId'
            handleFilterChanges={handleFilterChanges}
          />

          {/* From Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>From</Typography>
            <TextField
              type='text'
              value={filters.from || ''}
              size='small'
              placeholder='(555) 555-5555'
              sx={{ width: '180px' }}
              onChange={(e) => handleFilterChanges({ from: e.target.value })}
            />
          </Box>

          {/* To Filter */}
          <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
            <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>To</Typography>
            <TextField
              type='text'
              value={filters.to || ''}
              size='small'
              placeholder='(555) 555-5555'
              sx={{ width: '180px' }}
              onChange={(e) => handleFilterChanges({ to: e.target.value })}
            />
          </Box>

          {/* Date Filters & Refresh */}
          <Box display='flex' gap={1}>
            {/* Status Filter */}
            <Box display='flex' flexDirection='column' alignItems='flex-start' marginBottom={2}>
              <Typography sx={{ '&.MuiTypography-root': { fontSize: '13px' } }}>Status</Typography>
              <Autocomplete
                size='small'
                multiple
                options={Object.values(VoicemailStatus)}
                getOptionLabel={(option) => VOICEMAIL_STATUS_DISPLAY[option]}
                onChange={(_, value) => handleFilterChanges({ status: value })}
                value={filters.status || []}
                renderInput={(params) => (
                  <TextField {...params} placeholder='All status' sx={{ width: '200px' }} />
                )}
                sx={{ width: '200px' }}
              />
            </Box>

            {/* Created At */}
            <Box display='flex' flexDirection='column' alignItems='flex-start'>
              <Typography>Created At</Typography>
              <Box display='flex' alignItems='flex-start' gap={2}>
                <TextField
                  size='small'
                  type='date'
                  label='From Date'
                  value={filters.startDate || ''}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChanges({ startDate: value ?? undefined });
                  }}
                />
                <TextField
                  size='small'
                  type='date'
                  label='Until Date'
                  value={filters.endDate || ''}
                  InputLabelProps={{ shrink: true }}
                  onChange={(e) => {
                    const value = e.target.value;
                    handleFilterChanges({ endDate: value || undefined });
                  }}
                />
              </Box>
            </Box>

            {/* Clinic Filter */}
            <ClinicFilter
              selectedClinicIds={filters.clinicIds || []}
              includeUnassigned={!!filters.includeUnassigned}
              onChange={({ clinicIds, includeUnassigned }) =>
                handleFilterChanges({ clinicIds, includeUnassigned })
              }
            />

            {/* Refresh */}
            <Box
              display='flex'
              flexDirection='column'
              alignItems='center'
              justifyContent='center'
              className='messages-center-select'>
              <Box display='flex' alignItems='center' gap={2}>
                <RefreshIcon onClick={handleRefresh} sx={{ cursor: 'pointer' }} />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <CustomTable
        columns={[
          {
            key: 'action',
            label: <SwapVert />,
            width: '30px',
            minWidth: '30px',
          },
          { key: 'from', label: 'From', width: '150px', minWidth: '150px' },
          { key: 'to', label: 'To', width: '150px', minWidth: '150px' },
          { key: 'createdAt', label: 'Date/Time', width: '120px', minWidth: '120px' },
          { key: 'voicemail', label: 'Voicemail', width: '120px', minWidth: '120px' },
          { key: 'status', label: 'Status', width: '100px', minWidth: '100px' },
          { key: 'assignee', label: 'Assignee', width: '100px', minWidth: '100px' },
          { key: 'notes', label: 'Notes', width: '150px', minWidth: '150px' },
        ]}
        defaultText='No voicemails found'
        key='voicemail-center-table'
        loading={loading}
        rows={rows}
        pagination={{
          rowsPerPageOptions: [DEFAULT_PAGE_SIZE],
          rowPerPage: pagination.perPage || 1,
          page: pagination.currentPage || 1,
          totalCount: pagination.totalCount || 0,
          onPageChange: (page: number) => setPage(page),
        }}
      />

      {/* Voicemail Details Modal */}
      <VoicemailDetailModal
        open={isDetailModalOpen}
        voicemail={selectedVoicemail}
        onClose={() => {
          setIsDetailModalOpen(false);
          setSelectedVoicemail(null);
          // Drop the deep-link param so a refresh or back-navigation does not reopen the modal.
          clearDeepLink();
        }}
        onUpdate={handleVoicemailUpdate}
      />
    </Box>
  );
};
